import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import { typeColor } from '../lib/format.js';
import KopierKnopf from '../components/KopierKnopf.jsx';

/**
 * Einstufung nach dem Anteil der Liga, den das Exemplar hinter sich lässt.
 *
 * Bewusst das Perzentil und nicht der Rohwert: brauchbare Arten liegen alle
 * zwischen 78 und 93 Punkten, damit wäre jede Karte grün. Erst der Vergleich
 * mit der ganzen Liga trennt gut von mittelmäßig.
 */
const wertStufe = (p) =>
  p === null || p === undefined ? 'D'
    : p >= 95 ? 'S' : p >= 85 ? 'A' : p >= 70 ? 'B' : p >= 45 ? 'C' : 'D';

/**
 * Güte des eigenen IV-Musters, aus dem Rang-Perzentil.
 *
 * Wichtig für die Deutung: Rang und Kampfleistung haben verschiedene Skalen.
 * Zwischen Rang 1 und Rang 4096 liegen oft nur sechs Prozent Statprodukt.
 * Ein schlechter Rang heisst also "schlecht gewürfelt", nicht "unbrauchbar".
 */
const musterStufe = (pct) =>
  pct === null ? 'D' : pct >= 90 ? 'S' : pct >= 70 ? 'A' : pct >= 40 ? 'B' : 'D';

/** Urteil über das einzelne Exemplar — das ist die Aussage der Seite. */
const URTEIL = {
  build:   { farbe: 'var(--verdict-strong)', zeichen: '★' },
  covered: { farbe: 'var(--verdict-collection)', zeichen: '=' },
  maybe:   { farbe: 'var(--verdict-solid)', zeichen: '•' },
  skip:    { farbe: 'var(--verdict-collection)', zeichen: '–' },
  unrated: { farbe: 'var(--verdict-unrated)', zeichen: '?' },
};

const MUSTER_TEXT = {
  S: 'sehr gutes Muster',
  A: 'gutes Muster',
  B: 'mittelmässiges Muster',
  D: 'schwaches Muster',
};

const zahl = (n) => (n === null ? '—' : n.toLocaleString('de-DE'));

/**
 * Trainerkampf-Rangliste der eigenen Sammlung, je Liga von bester zu
 * schlechtester Bewertung.
 */
export default function MyPvpPage() {
  const [daten, setDaten] = useState(null);
  const [liga, setLiga] = useState('great');
  const [fehler, setFehler] = useState(null);

  useEffect(() => {
    api.myPvp().then(setDaten).catch((e) => setFehler(e.message));
  }, []);

  if (fehler) return <div className="error">{fehler}</div>;
  if (!daten) return <div className="skeleton" style={{ height: 200 }} />;

  if (daten.total === 0) {
    return (
      <div className="empty">
        <div className="empty__icon">⚔</div>
        <p><strong>Keine Trainerkampf-Werte im Import.</strong></p>
        <p>
          In Poke Genie die PvP-Ränge aktivieren und neu exportieren, dann
          erscheinen sie hier.
        </p>
      </div>
    );
  }

  const aktuell = daten.leagues.find((l) => l.key === liga) ?? daten.leagues[0];

  return (
    <>
      <div className="filters">
        {daten.leagues.map((l) => (
          <button
            key={l.key}
            type="button"
            className={liga === l.key ? 'chip chip--on' : 'chip'}
            onClick={() => setLiga(l.key)}
          >
            {l.name} ({l.count})
          </button>
        ))}
      </div>

      {aktuell.teamSuche && (
        <div className="suchtext">
          <div>
            <strong>Deine {aktuell.teamAnzahl} Besten auf einmal</strong>
            <div className="card__meta">
              Namen und genaue WP-Werte. Was der Suche fehlt, ist die feste
              Zuordnung beider — es können also Gleichnamige mit zufällig
              passenden WP dazwischenrutschen.
            </div>
          </div>
          <KopierKnopf text={aktuell.teamSuche} label="Kopieren" title={aktuell.teamSuche} />
        </div>
      )}

      {aktuell.count === 0 ? (
        <div className="empty">Für diese Liga hat der Export nichts geliefert.</div>
      ) : (
        <div className="stack">
          {aktuell.pokemon.map((p, i) => <RangZeile key={p.id + aktuell.key} mon={p} platz={i + 1} />)}
        </div>
      )}
    </>
  );
}

function RangZeile({ mon, platz }) {
  const stufe = wertStufe(mon.effectivePerzentil);
  const urteil = URTEIL[mon.verdict?.level] ?? URTEIL.unrated;

  return (
    <article className="rang-zeile">
      <div className="rang-zeile__platz">{platz}</div>

      <div className="rang-zeile__mitte">
        <div className="spread">
          <div>
            <span className="rang-zeile__name">
              {mon.catalogSlug
                ? <Link to={'/pokemon/' + mon.catalogSlug}>{mon.name}</Link>
                : mon.name}
              {mon.form && <span className="mon-card__form"> {mon.form}</span>}
              {mon.gender && <span className="text-faint"> {mon.gender}</span>}
            </span>
            {mon.target && <div className="card__meta">bewertet als {mon.target}</div>}
          </div>
          <div className="rang-zeile__wert">
            <span className={'rang-zeile__gesamt tier--' + stufe}>
              {mon.effectiveRank ? '~' + zahl(mon.effectiveRank) : '—'}
            </span>
            <span className="rang-zeile__wert-label">
              {mon.ligaUmfang ? 'von ' + zahl(mon.ligaUmfang) : 'Liga-Platz'}
            </span>
          </div>
        </div>

        {mon.effectivePerzentil !== null && mon.effectivePerzentil !== undefined && (
          <p className="rang-zeile__einordnung">
            Kämpft ungefähr wie ein durchschnittliches Exemplar von Liga-Platz{' '}
            {zahl(mon.effectiveRank)} — besser als {mon.effectivePerzentil} % der Liga.
            <span className="text-faint"> (Wert {mon.effective})</span>
          </p>
        )}

        <div className="werte">
          <div className="werte__zeile">
            <span className="werte__label">Art in der Liga</span>
            <span className="werte__balken">
              <i style={{ width: (mon.artPerzentil ?? 0) + '%' }} />
            </span>
            <span className="werte__zahl">
              {mon.artPct !== null ? mon.artPct : '—'}
            </span>
            <span className="werte__zusatz">
              {mon.speciesRank ? 'Platz ' + zahl(mon.speciesRank) + ' · Skala 0–100' : 'Skala 0–100'}
            </span>
          </div>

          <div className="werte__zeile">
            <span className="werte__label">Exemplar in der Art</span>
            <span className="werte__balken">
              <i style={{ width: (mon.exemplarPerzentil ?? 0) + '%' }} />
            </span>
            <span className="werte__zahl">
              {mon.exemplarPct !== null ? mon.exemplarPct + ' %' : '—'}
            </span>
            <span className="werte__zusatz">
              {mon.rank ? 'Rang ' + zahl(mon.rank) + ' von 4096' : ''}
            </span>
          </div>
        </div>

        {mon.betterSameType && !mon.betterSameSpecies && (
          <p className="rang-zeile__belegt">
            Typ {mon.betterSameType.typ} ist bei dir schon von {mon.betterSameType.name} besetzt
            ({mon.betterSameType.wert}) — in ein Team passen drei, und die konkurrieren
            über den Typ.
          </p>
        )}

        {mon.attacken && (
          <p className="rang-zeile__attacken">
            <span className="text-faint">Attacken: </span>{mon.attacken}
          </p>
        )}

        <div className="inline">
          {mon.suche && (
            <KopierKnopf
              text={mon.suche}
              label="Im Spiel finden"
              title={mon.suche}
            />
          )}
          <span className="badge" style={{ '--badge-color': urteil.farbe }}>
            {urteil.zeichen} {mon.verdict.label}
          </span>
          <span className={'badge badge--muster tier--' + musterStufe(mon.rankPct)}>
            {MUSTER_TEXT[musterStufe(mon.rankPct)]}
          </span>
          <span className="badge badge--soft">WP {mon.cp}</span>
          {mon.ivs && <span className="badge badge--soft">{mon.ivs.join('/')}</span>}
          {mon.isShadow && <span className="badge badge--soft">Crypto</span>}
          {mon.isPurified && <span className="badge badge--soft">Erlöst</span>}
          {mon.isLucky && <span className="badge badge--soft">Glücks</span>}
        </div>

        {mon.verdict?.reason && (
          <p className={'rang-zeile__urteil rang-zeile__urteil--' + mon.verdict.level}>
            {mon.verdict.reason}
          </p>
        )}

        <div className="inline rang-zeile__kosten">
          {mon.types.map((t) => (
            <span className="type" key={t} style={{ '--type-color': typeColor(t) }}>{t}</span>
          ))}
          <span className="text-faint">
            Ausbau: {zahl(mon.dustCost)} Sternenstaub · {zahl(mon.candyCost)} Bonbons
          </span>
        </div>
      </div>
    </article>
  );
}
